import type { TaskPrefill } from '@/contexts/TaskDrawerContext'
import { caseService } from '@/services/case-service'
import { clientService } from '@/services/client-service'

/** Campos mínimos da publicação (ficha completa ou item da caixa de entrada). */
export interface PublicationForTask {
  id: number | string
  tipoComunicacao?: string
  numeroProcessoComMascara?: string
  numeroProcesso?: string
  siglaTribunal?: string
  nomeOrgao?: string
  texto?: string
  linkedCaseId?: string | null
  linked_case_id?: string | null
  linked_client_id?: string | null
}

type ClientLike = { clientType?: string; personalData?: { fullName?: string }; companyData?: { tradingName?: string; companyName?: string } }

function clientNameOf(client: ClientLike | null | undefined): string | undefined {
  if (!client) return undefined
  if (client.clientType === 'PERSON') return client.personalData?.fullName
  if (client.clientType === 'COMPANY') return client.companyData?.tradingName || client.companyData?.companyName
  return undefined
}

/**
 * Pré-preenchimento da gaveta a partir de uma publicação: vínculo com a
 * publicação (o servidor marca "prazo lançado" por evento) e, se a publicação
 * estiver ligada a um processo, o processo e o cliente.
 */
export async function prefillFromPublication(publication: PublicationForTask): Promise<TaskPrefill> {
  const numero = publication.numeroProcessoComMascara ?? publication.numeroProcesso ?? ''
  const prefill: TaskPrefill = {
    title: publication.tipoComunicacao ? `Analisar ${publication.tipoComunicacao.toLowerCase()} — ${numero}` : `Analisar publicação — ${numero}`,
    description: [
      `Publicação DJEN: ${publication.tipoComunicacao ?? '—'}`,
      `Tribunal: ${publication.siglaTribunal ?? '—'}${publication.nomeOrgao ? ` · ${publication.nomeOrgao}` : ''}`,
      publication.texto ? `\n${publication.texto.slice(0, 500)}${publication.texto.length > 500 ? '…' : ''}` : '',
    ].join('\n').trim(),
    category: 'PRAZO_PROCESSUAL',
    prioridade: 'P0',
    publication_id: Number(publication.id),
    origin_type: 'from_publication',
    origin_id: String(publication.id),
    tags: ['DJEN'],
    origemLabel: `Publicação ${numero}`,
  }

  const caseId = publication.linkedCaseId || publication.linked_case_id
  try {
    if (caseId) {
      const found = await caseService.getCaseById(caseId)
      const clientId = publication.linked_client_id || found?.clientId
      if (found?.id && clientId) {
        const client = await clientService.getClientById(clientId).catch(() => null)
        Object.assign(prefill, {
          case_id: found.id,
          case_title: found.title,
          case_number: found.courtNumber || found.caseNumber,
          client_id: clientId,
          client_name: clientNameOf(client as ClientLike | null) ?? found.client?.name,
        })
      }
    } else if (publication.linked_client_id) {
      const client = await clientService.getClientById(publication.linked_client_id).catch(() => null)
      Object.assign(prefill, { client_id: publication.linked_client_id, client_name: clientNameOf(client as ClientLike | null) })
    }
  } catch {
    // Sem vínculo: a tarefa nasce só com a publicação.
  }
  return prefill
}
